import React, { useState } from 'react';
import { Button } from '../../ui/components/Button';
import {
  FeatherEdit2,
  FeatherZap,
  FeatherClock,
  FeatherTag,
} from '@subframe/core';
import { MutateNFTModal } from './MutateNFTModal';
import type { MutationResult } from '../../api/mutations';

interface NFTData {
  nft_token_id: string;
  name: string;
  description?: string;
  image_url?: string;
  media_url?: string;
  thumbnail_url?: string;
  traits?: Array<{ trait_type: string; value: string | number }>;
  traits_with_rarity?: Array<{ trait_type: string; value: string | number; rarity_percentage?: number }>;
  is_mutable?: boolean;
  metadata_version?: number;
  metadata_updated_at?: string;
  /** Full metadata JSON — forwarded so MutateNFTModal can read properties.files. */
  metadata?: {
    image?: string;
    properties?: {
      files?: Array<{ uri?: string; type?: string; cdn_url?: string }>;
    };
  } & Record<string, unknown>;
}

interface CreatorToolsSectionProps {
  nft: NFTData;
  /** Whether mutation is available (nft.is_mutable) */
  canMutate: boolean;
  /** Whether tag editing is available (canManageCollection && collectionTags.length > 0) */
  canEditTags: boolean;
  /** Whether there are existing tag values on this NFT */
  hasCustomTags: boolean;
  /** Whether the current user is the NFT issuer (vs a project manager) */
  isIssuer: boolean;
  onMutationSuccess: (result: MutationResult) => void;
  onAuthorizationRequired: (requiredMinter: string) => void;
  onEditTags: () => void;
}

export const CreatorToolsSection: React.FC<CreatorToolsSectionProps> = ({
  nft,
  canMutate,
  canEditTags,
  hasCustomTags,
  isIssuer,
  onMutationSuccess,
  onAuthorizationRequired,
  onEditTags,
}) => {
  const [showMutateModal, setShowMutateModal] = useState(false);

  const handleMutationSuccess = (result: MutationResult) => {
    setShowMutateModal(false);
    onMutationSuccess(result);
  };

  return (
    <>
      <div className="flex w-full items-center justify-between rounded-lg border border-brand-200 bg-brand-50 px-4 py-3">
        <div className="flex items-center gap-3">
          <FeatherZap className="h-4 w-4 text-brand-600" />
          <span className="text-body-bold font-body-bold text-brand-700">You can manage this NFT</span>
          {canMutate && nft.metadata_version && nft.metadata_version > 1 && (
            <div className="hidden sm:flex items-center gap-1.5 text-caption font-caption text-brand-600">
              <FeatherClock className="h-3 w-3" />
              <span>
                v{nft.metadata_version}
                {nft.metadata_updated_at && (
                  <> &middot; Last mutated {new Date(nft.metadata_updated_at).toLocaleDateString()}</>
                )}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canEditTags && (
            <Button
              variant="neutral-secondary"
              size="small"
              icon={<FeatherTag className="h-3.5 w-3.5" />}
              onClick={onEditTags}
            >
              {hasCustomTags ? 'Edit Tags' : 'Add Tags'}
            </Button>
          )}
          {canMutate && (
            <Button
              variant="brand-secondary"
              size="small"
              icon={<FeatherEdit2 className="h-3.5 w-3.5" />}
              onClick={() => setShowMutateModal(true)}
            >
              Mutate NFT
            </Button>
          )}
        </div>
      </div>

      {canMutate && (
        <MutateNFTModal
          open={showMutateModal}
          onClose={() => setShowMutateModal(false)}
          nft={nft}
          isIssuer={isIssuer}
          onSuccess={handleMutationSuccess}
          onAuthorizationRequired={onAuthorizationRequired}
        />
      )}
    </>
  );
};
